import React, { Fragment, useContext, useEffect, useRef, useState } from "react"
import { Alert } from "react-bootstrap"

import useLog from "../../hooks/useLog"
import { Row } from "../../components/Row"
import Indent from "../../components/Indent"
import { Loader } from "../../components/Loader"
import SkeletonText from "../../components/SkeletonText"
import { Spaces } from "../../components/Spaces"
import { Tableau } from "../../components/Tableau"
import SwitchSlider from "../../components/SwitchSlider.js"
import { TRANSACTIONS_PAGE_LIMIT } from "../../display_wallet"
import { _sha256 } from "../../helpers/helpers"
import { UserContext } from "../../Contexts/UserContext/UserContext"
import { TextDisplayToken } from "../../components/TextDisplay/TextDisplay"
import { UnorderedListOutlined } from "@ant-design/icons"
import Constants from "../../config.js"
import { ButtonPrimary } from "../../components/Buttons.js"

// how many raw txs we keep enriched ahead of the visible window.
// bounds background work so big wallets don't enrich everything upfront,
// while hiding the ~2/sec WoC throttle from the user when fee txs are hidden.
const LOAD_BUFFER = 20


/**
 *  TODO cache the history & txdata
 */
export default function Slide_History_BSV(props) {
    const classname = "Slide_History_BSV: "
    const function_id = classname
    let log = useLog(function_id)

    const userContext = useContext(UserContext)
    const purse_address = userContext.address_purse
    const bsvprice = userContext.bsvprice

    const [nbloaded, set_nbloaded] = useState(null)
    const [history, set_history] = useState(null)
    // display_limit: number of VISIBLE (filtered) rows to show. Raised by "Load more".
    const [display_limit, set_display_limit] = useState(TRANSACTIONS_PAGE_LIMIT)
    // load_limit: how many raw txs to enrich in the background. Stays a buffer
    // ahead of display_limit so "Load more" usually reveals rows instantly.
    const [load_limit, set_load_limit] = useState(TRANSACTIONS_PAGE_LIMIT + LOAD_BUFFER)

    const [started_fetch, set_started_fetch] = useState(false)
    const [tokens_txdata, set_tokens_txdata] = useState(null)

    // TODO allow user to modify this param
    const [threshold_eur, set_threshold_eur] = useState(0.00)
    const [hideTokenTxs, set_hideTokenTxs] = useState(true)

    const lockRef = useRef(null)
    // true while the current mount was restored from the UserContext cache
    // (raw list and/or enriched txdata). Prevents the loader-gating effect
    // below from re-arming the spinner on a partial cache restore.
    const from_cache_ref = useRef(false)

    log("started_fetch=", started_fetch, "\n bsvprice=", bsvprice) //,"\n tokens_txdata=", tokens_txdata)

    /**
     * Enrich (fetch per-tx details for) raw txs from history, up to forced_load_limit.
     * Background loading is driven by load_limit (display_limit + LOAD_BUFFER),
     * decoupled from the filtered display so hiding fee txs never blocks "Load more".
     *
     * @param {int} forced_load_limit - how many raw txs to have enriched after this call
     * @returns
     */
    const load_more_txdata_cf_pagelimit = async (forced_load_limit = null) => {
        let log = useLog(function_id + "load_more_txdata_cf_pagelimit(): ")
        const _load_limit = forced_load_limit || load_limit

        if (history == null) {
            log("history is null, aborting")
            return
        }
        // make sure we only execute 1 at a time
        log("lockRef.current=", lockRef.current)
        if (lockRef.current) return
        else lockRef.current = true

        log("called, _load_limit=", _load_limit, "nbloaded:", nbloaded)
        if (tokens_txdata) log("tokens_txdata.length=", tokens_txdata.length)
        else log("tokens_txdata is null")

        let curr_size_tokens_txdata = tokens_txdata ? tokens_txdata.length : 0
        const target = Math.min(_load_limit, history.length)
        // nothing to do if we already have everything up to the load limit (or the whole history)
        if (curr_size_tokens_txdata >= target) {
            log("nothing to load, freeing lock")
            lockRef.current = false
            return
        }
        const history_to_load = history.slice(curr_size_tokens_txdata, target)

        // Process the batch SEQUENTIALLY (await each), not via Promise.all.
        // Rationale: the WoC connector's rate limiter is a time-based queue
        // (spaces request *starts* by minIntervalMs). Sequential awaiting
        // keeps at most 1 request in-flight per interval, which is the safest
        // pattern and avoids overwhelming the queue. Throughput is identical
        // (rate-bound at 3/sec) either way.
        const new_tokens_txdata = []
        for (let tx_data of history_to_load) {
            try {
                // route through the UserContext helper so the IndexedDB-backed
                // BsvHistoryCache short-circuits the WoC fetchTxInfo call for
                // already-enriched txs (write-through on miss).
                const processedTxData = await userContext.enrich_one_bsv_tx(tx_data, purse_address)
                if (processedTxData && processedTxData.tx_hash) {
                    new_tokens_txdata.push(processedTxData)
                }
            } catch (err) {
                log("processOneTxData failed for tx=", tx_data && tx_data.tx_hash, err)
            }
        }
        log("processed batch, new_tokens_txdata.length=", new_tokens_txdata.length)

        // append to the UserContext cache (dedup by tx_hash)
        if (new_tokens_txdata.length > 0) userContext.cache_history_bsv(new_tokens_txdata)

        // concat the existing txdata with the new one (dedup by tx_hash)
        let all_tokens_txdata = tokens_txdata || []
        if (tokens_txdata) {
            const existing_txhs = tokens_txdata.map(e => e.tx_hash)
            const nodups_new_tokens_txdata = new_tokens_txdata.filter(
                e => e.tx_hash && !existing_txhs.includes(e.tx_hash)
            )
            if (nodups_new_tokens_txdata.length > 0) {
                all_tokens_txdata = tokens_txdata.concat(nodups_new_tokens_txdata)
            }
        } else {
            all_tokens_txdata = new_tokens_txdata
        }

        if (all_tokens_txdata !== tokens_txdata) {
            set_tokens_txdata(all_tokens_txdata)
        }
        log("finished")
        lockRef.current = false  // free the lock
    }

    // Display derivation is now computed in render (see display_list below),
    // so update_filtered_txdata and the tokens_txdata_filtered state are gone.
    // The hide-fee toggle only swaps which view is rendered; it never affects
    // loading or the "Load more" button.

    // TODO add this to userContext & reload it when reload button is clicked + bonus: cache it on disk
    const fetch_history = async () => {
        log("fetch_history: start, started_fetch=", started_fetch, ", bsvprice=", bsvprice)

        // Cache: both the raw tx-hash list (from WoC) and the enriched txdata are
        // cached in UserContext (mirrors how token history persists in context).
        // On navigation back, if both caches exist we restore instantly and skip
        // the WoC refetch entirely — no loading delay. Cache is invalidated on
        // wallet reload / send, so it's always fresh enough for display.
        const cached_raw = userContext.get_history_bsv_raw()
        const cached_txdata = userContext.get_history_bsv()

        // Stale-while-revalidate: when we restore from the cached raw list we
        // ALSO fire a non-awaited WoC refetch in the background to catch txs
        // received since the last visit (the cache snapshot is point-in-time).
        // If the fresh list differs (length or newest tx_hash), we update the
        // context cache + local state so new txs appear without a manual
        // refresh. Skipped for the mock wallet (no real history on chain).
        // fetch_bsv_history_raw() already has single-flight + 3s freshness
        // dedup, so this is safe to call from every cache-hit path.
        const revalidate_in_background = (cached) => {
            if (!cached || userContext.isMockWallet === true) return
            userContext.fetch_bsv_history_raw(purse_address)
                .then(fresh => {
                    if (!fresh || !Array.isArray(fresh)) return
                    const cached_newest = cached[0] && cached[0].tx_hash
                    const fresh_newest = fresh[0] && fresh[0].tx_hash
                    if (fresh.length !== cached.length || fresh_newest !== cached_newest) {
                        log("revalidate: list changed (cached=" + cached.length
                            + ", fresh=" + fresh.length + "), updating")
                        userContext.cache_history_bsv_raw(fresh)
                        set_history(fresh)
                        set_nbloaded(fresh.length)
                    } else {
                        log("revalidate: list unchanged, no update")
                    }
                })
                .catch(() => { /* non-critical, stays on cached list */ })
        }

        if (cached_raw && cached_txdata && cached_txdata.length > 0) {
            log("fetch_history: found cached raw + txdata, restoring without refetch")
            from_cache_ref.current = true
            set_tokens_txdata(cached_txdata)
            set_nbloaded(cached_raw.length)
            set_is_loading(false)  // cache hit: data is already displayable, no loading screen
            set_history(cached_raw)
            revalidate_in_background(cached_raw)
            return  // skip blocking WoC refetch; buffer refill + revalidate happen in background
        }
        if (cached_raw) {
            // raw list cached but no txdata enriched yet (user left the page
            // very early, or cache was reset by a send). Restore the raw list
            // instantly and let background enrichment repopulate txdata without
            // a blocking WoC refetch.
            log("fetch_history: found cached raw (no txdata), restoring raw, enriching in background")
            from_cache_ref.current = true
            set_nbloaded(cached_raw.length)
            set_history(cached_raw)
            revalidate_in_background(cached_raw)
            return
        }
        if (cached_txdata) {
            log("fetch_history: found cached txdata (no raw), will refetch raw list")
            set_tokens_txdata(cached_txdata)
            set_nbloaded(cached_txdata.length)
        } else {
            set_nbloaded(0)
        }

        // TODO handle pages for addresses with a very big history
        // NOTE: WoC returns history newest-first; we don't reverse here because
        // the display layer sorts explicitly by time descending.
        // Use the shared single-flight helper from UserContext so concurrent
        // _refresh_bsv_history() calls don't double-fetch the same WoC endpoint.
        let history = await userContext.fetch_bsv_history_raw(purse_address)
        log("fetch_history: history.length=", history.length, "history=", history, "history.length=", history.length)

        // store raw list in context cache so navigation back is instant
        userContext.cache_history_bsv_raw(history)

        set_nbloaded(history.length)
        set_history(history)
    }

    const changeHideTokenTxs = () => {
        set_hideTokenTxs(!hideTokenTxs)
    }
    // No effect needed: hide-fee only re-renders the display_list (computed in render).

    // componentDidMount
    useEffect(() => {
        if (!started_fetch) {
            set_started_fetch(true)

            // if (!userContext.isMockWallet) fetch_history()
            // else set_history([])
            fetch_history()
        }
    }, [])  // empty for componentDidMount


    // Pick up raw-list growth injected into the context cache (e.g. after a
    // BSV send or a background reload_bsv). forceReRender() (called after sends
    // and at the end of _refresh_bsv_history) bumps reloadCount, triggering
    // this effect. The DISPLAY rows are now read directly from the cache in
    // render (see display_txdata below), so we no longer merge enriched entries
    // into local state here — that was the source of the stale "loading 1/61"
    // bug (local state lagging the cache during a background refresh). We only
    // sync the raw list so the buffer-fill effect sees the grown history.
    // NOTE: userContext IS the state object (Provider value={this.state}), so we
    // access reloadCount directly, not via userContext.state.reloadCount.
    const prevReloadCountRef = useRef(userContext.reloadCount || 0)
    useEffect(() => {
        const currReloadCount = userContext.reloadCount || 0
        if (prevReloadCountRef.current === currReloadCount) return
        prevReloadCountRef.current = currReloadCount

        // sync the raw list so nbloaded and the buffer-fill effect see growth
        const cached_raw = userContext.get_history_bsv_raw()
        if (cached_raw) {
            set_history(cached_raw)
            set_nbloaded(cached_raw.length)
        }
    }, [userContext.reloadCount])


    // Background enrichment: keep a buffer of NON-FEE txs ahead of display_limit.
    // "Load more" always reveals X non-fee txs, so we enrich raw txs until the
    // non-fee count reaches display_limit + LOAD_BUFFER (or all raw txs are done).
    // We don't know which raw txs are fee txs until enriched, so we enrich in
    // raw chunks of LOAD_BUFFER and re-check after each chunk. The hide-fee
    // toggle has NO influence here (it only swaps the rendered view).
    useEffect(() => {
        if (history == null || history.length === 0) return
        const nonfee_count = tokens_txdata ? tokens_txdata.filter(e => e.action != "token_tx").length : 0
        const all_enriched = tokens_txdata != null && tokens_txdata.length >= history.length
        const desired_nonfee = display_limit + LOAD_BUFFER
        if (nonfee_count >= desired_nonfee || all_enriched) return  // buffer satisfied
        // enrich the next raw chunk toward satisfying the non-fee buffer
        const desired_raw = Math.min((tokens_txdata ? tokens_txdata.length : 0) + LOAD_BUFFER, history.length)
        if (desired_raw > load_limit) set_load_limit(desired_raw)
        load_more_txdata_cf_pagelimit(desired_raw)
    }, [history, display_limit, tokens_txdata])


    const handleSeeMore = () => {
        set_display_limit(display_limit + TRANSACTIONS_PAGE_LIMIT)
    }

    // --- Derived data (computed in render; no separate filtered state) ---
    // Pagination is anchored to NON-FEE txs: "Load more" always reveals X
    // non-fee txs. The hide-fee toggle only swaps which view is rendered and
    // has ZERO effect on loading or the "Load more" button.
    //
    // DISPLAY rows are read directly from the persistent UserContext cache
    // (userContext.get_history_bsv()), mirroring how token history reads
    // userContext.tokens_txdata. The cache is never reset on a send (only on
    // wallet/network change), so rows stay visible during a background
    // reload_bsv instead of being wiped to "loading 1/61". The local
    // tokens_txdata state is kept ONLY as the enrichment-progress tracker
    // driving load_more_txdata_cf_pagelimit + the buffer-fill effect; its
    // set_tokens_txdata re-renders, so the cache-driven display refreshes in
    // lockstep as enrichment appends to the cache.
    const display_txdata = userContext.get_history_bsv() || tokens_txdata
    const display_txdata_nonfee = display_txdata
        ? display_txdata.filter(e => e.action != "token_tx")
        : []
    const all_enriched = history && tokens_txdata && tokens_txdata.length >= history.length

    // raw cutoff = raw index covering `display_limit` non-fee txs (inclusive).
    // When fee txs are shown, every tx (fee or not) up to this index is rendered,
    // so toggling hide off just reveals the interspersed fee rows without changing
    // the non-fee page.
    let raw_cutoff = 0
    if (display_txdata) {
        let nonfee_seen = 0
        for (let i = 0; i < display_txdata.length && nonfee_seen < display_limit; i++) {
            if (display_txdata[i].action != "token_tx") nonfee_seen++
            raw_cutoff = i + 1
        }
    }

    let display_list = []
    if (hideTokenTxs) {
        display_list = display_txdata_nonfee.slice(0, display_limit)
    } else {
        display_list = display_txdata ? display_txdata.slice(0, raw_cutoff) : []
    }
    // threshold filter (off by default) applied to the final view
    if (threshold_eur && threshold_eur > 0 && display_list.length > 0) {
        display_list = display_list.filter(e => e.value_bsv * bsvprice > threshold_eur)
    }

    // sort by time descending so latest txs appear first, regardless of the
    // order WoC returned or the order enrichment ran in.
    display_list.sort((a, b) => (b.time || 0) - (a.time || 0))

    if (display_txdata) {
        log("| display_list=", display_list.length,
            "/ nonfee=", display_txdata_nonfee.length,
            "/ display_txdata.length=", display_txdata.length,
            "| threshold_eur=", threshold_eur)
    }

    // format transactions for display
    var buttontitle = ""
    const multlist2d = []
    for (let i = 0; i < display_list.length; i++) {
        let token_txdata = display_list[i]
        log("token_txdata=", JSON.stringify(token_txdata))

        let action = token_txdata.action
        let sender = token_txdata.sender
        let receiver = token_txdata.receiver

        const Red = (props) => {
            return <div style={{ color: "red" }}>
                {props.children}
            </div>
        }
        const Green = (props) => {
            return <div style={{ color: "#00cc00", fontWeight: "bold" }}>
                {props.children}
            </div>
        }

        let txhash = token_txdata.tx_hash
        const _date_obj = new Date(token_txdata.time)
        const date_str = isNaN(_date_obj) ? "—" : _date_obj.toLocaleString("fr-FR")
        const txhash_url = "https://whatsonchain.com/tx/" + txhash
        let date = <Fragment>
            <a href={txhash_url} target="_blank">
                {date_str}
            </a>
        </Fragment>

        let value_eur = parseFloat(token_txdata.value_bsv * bsvprice).toFixed(2)
        let address = "", value = ""

        if (action == "token_tx") {
            date = <div> {date} </div>
            address = <div>fee for token transaction</div>
            if (value_eur < 0.005) {
                value = <div> -{value_eur} €</div>
            } else {
                value = <div> -{value_eur} € ({token_txdata.value_bsv} BSV)</div>
            }
        }
        else if (action == "send") {
            date = <Red> {date} </Red>
            address = <Red>{receiver}</Red>
            value = <Red> -{value_eur} € ({token_txdata.value_bsv} BSV)</Red>
        }
        else if (action == "receive") {
            date = <Green> {date} </Green>
            address = <Green>{sender}</Green>
            value = <Green>+{value_eur} € ({token_txdata.value_bsv} BSV)</Green>
        }
        else if (action == "send_to_yourself") {
            address = <div>sent to yourself</div>
            value = <div>↺ {value_eur} € ({token_txdata.value_bsv} BSV)</div>
        }
        let dataline = [date, value, address]
        multlist2d.push(dataline)
    }
    // "Load more" reveals the next page; the counter shows TOTAL remaining raw txs.
    const remaining_total = history ? Math.max(0, history.length - raw_cutoff) : 0
    if (remaining_total > 0) {
        // buttontitle = "Load more (" + remaining_total + " remaining)"
        buttontitle = "Show more"
    }

    const [is_loading, set_is_loading] = useState(() => {
        // start without the spinner when a cache hit is guaranteed, so
        // navigating back to the page doesn't flash the loader before
        // fetch_history's cache-hit branch runs.
        const c = userContext.get_history_bsv()
        return !(c && c.length > 0)
    })
    // is_loading gates the INITIAL load only (table loader / progress bar).
    // It clears as soon as we have enough NON-FEE txs for the first display page
    // (display_limit), NOT the full preload buffer — the buffer refills silently
    // in the background so a cache restore with a partial buffer doesn't re-block
    // the UI. On a fresh load (no cache) this still waits for the first page.
    useEffect(() => {
        if (!history) return                       // still fetching the tx-hash list
        if (history.length === 0) { set_is_loading(false); return }  // no txs at all
        if (tokens_txdata == null) {
            // raw-only cache restore: keep the spinner until enrichment
            // produces rows, but don't fall back to the fresh-fetch path.
            if (!from_cache_ref.current) set_is_loading(true)
            return
        }
        if (from_cache_ref.current) {
            // cache restore: displayable as soon as ANY enriched rows exist;
            // don't re-block waiting for the full first page (background
            // enrichment continues silently via the buffer-fill effect above).
            set_is_loading(tokens_txdata.length === 0)
            return
        }
        const page_ready = display_txdata_nonfee.length >= display_limit
        set_is_loading(!page_ready && !all_enriched)
    }, [history, tokens_txdata, display_limit, all_enriched, display_txdata_nonfee.length])

    // see-more button
    let seemorebutton = null
    // loading_more = the user raised display_limit but we don't yet have enough
    // non-fee txs enriched to show them (buffer lagging, e.g. rapid clicks). Shows
    // a spinner on the button for immediate feedback while enrichment catches up.
    const loading_more = display_txdata != null
        && display_txdata_nonfee.length < display_limit
        && !all_enriched
        && remaining_total > 0
    if (history && history.length > 0 && remaining_total > 0) {
        seemorebutton = <center>
            <ButtonPrimary onClick={loading_more ? null : handleSeeMore}
                disabled={loading_more}>
                {loading_more ? <>loading more... <Loader small /></> : buttontitle}
            </ButtonPrimary>
        </center>
    }

    let hasMoreTxs = remaining_total > 0

    var titles = [
        "Date",
        <Fragment>Amount</Fragment>,
        "from/to"
    ]

    log("\n is_loading=", is_loading,
        "\n multlist2d.length=", multlist2d.length,
        "\n nbloaded=", nbloaded,
    )

    // full table loader only when we have NO rows at all AND the wallet isn't
    // empty (i.e. initial load with no cache). During a background reload_bsv
    // we always have cached rows, so the table never blanks — only the small
    // inline "loading X/Y..." badge shows (mirrors token history).
    const readyToDisplay = multlist2d.length > 0 || (history != null && history.length === 0)

    // fee_tx_count = how many MORE rows would appear if the user un-hides fee
    // txs, i.e. the fee txs inside the current visible window (the raw_cutoff
    // slice shown when hide is off). NOT the total fee txs across the whole
    // cache — unhiding only reveals the fee rows interspersed in this window.
    const visible_window = display_txdata ? display_txdata.slice(0, raw_cutoff) : []
    const fee_tx_count = visible_window.filter(e => e.action == "token_tx").length
    const hide_fee_label = "Hide " + fee_tx_count + " fee transactions"

    // inline "loading..." indicator shown next to the tx count header when
    // a background refresh is running AND we already have rows to display.
    const loadingCount = userContext.isloading && multlist2d.length > 0
    // numerator comes from the persistent cache (not local enrichment state)
    // so it reflects what's actually displayable and never shows a misleading
    // "loading 1/61" while the cache has all 61 enriched rows.
    const enriched_count = display_txdata ? display_txdata.length : 0

    return (<div>
        <h1><UnorderedListOutlined /><Spaces count="3" />
            History of your BSV transactions
        </h1><br />
        <center>
            <TextDisplayToken>
                Address<Indent />
                {purse_address ? <>
                    {userContext.isMockWallet ?
                        Constants.MOCK_WALLET_PHRASE
                        : <b>bsv:{purse_address}</b>}
                </> : <SkeletonText width="10em" />}
            </TextDisplayToken>
        </center><br />

        <Alert variant="light">
            <i style={{ fontSize: "25px", marginRight: "10px" }} className="fa fa-info-circle" />
            {/* Les transactions très récentes peuvent mettre quelques secondes à s'afficher,
            utilisez le bouton "Reload BSV" dans le menu de gauche ou sur la page de solde
            pour resynchroniser. */}
            Very recent transactions may take a few seconds to display.
            Use the "Reload BSV" button (sidebar or Balance page) to refresh.
        </Alert><br />

        {history ?
            <Fragment>
                <h4>{nbloaded != null ? nbloaded : '?'} BSV Transactions
                    {loadingCount && (
                        <span style={{ fontSize: "smaller", marginLeft: "15px" }}>
                            {nbloaded != null && nbloaded!=enriched_count?<>
                                loading {enriched_count}/{nbloaded}...
                            </>:<>
                                    loading...
                            </>}
                            <i className="fas fa-sync fa-spin" style={{ marginLeft: "5px" }} />
                        </span>
                    )}
                </h4>
                <br />
            </Fragment>
            : null}

        <Row><Tableau
            titles={titles}
            values={multlist2d}
            loading={!readyToDisplay}
            empty_mess="No transaction found."
        /> </Row>

        {readyToDisplay ?
            hasMoreTxs ? seemorebutton : null
            : null}

        {readyToDisplay && nbloaded > 0 ?
            <div style={{ width: "100%", display: "inline-block" }}>
                <br /><div style={{ display: "inline-block", float: "right" }}>
                    <SwitchSlider onChange={changeHideTokenTxs} checked={hideTokenTxs} />
                    &nbsp;<i>{hide_fee_label}</i>
                </div>
            </div>
            : null}
        <br />

        {threshold_eur && threshold_eur > 0 ?
            <i style={{ fontSize: "smaller" }}>
                {/* *Les transactions d'une valeur inférieure à {threshold_eur}€ ne sont pas affichées */}
                *Transactions valued at less than {threshold_eur}€ are not displayed.
            </i>
            : null}
    </div>)
}
